/**
 * nearbyStats.ts — T-451, the "Nearby" statistics Noreen asked for on the PDF one-pager.
 *
 * Noreen's ask: statistics about charging competitors and local amenities within a distance of
 * the searched site. Her old tool measured at 2 miles and she judged that too wide, so this
 * presents SEVERAL radii side by side rather than betting on one; which radii ship is one
 * exported constant away.
 *
 * WHERE THE DATA COMES FROM, AND WHY IT IS NOT THE SITE SCORE. The obvious source is the
 * `/score/site` response the export already holds — and it is the wrong one. That path filters
 * POIs to the handful of ALS categories that feed the amenity term of the score, and never
 * fetches Hospital or University at all: the two categories Noreen ranks ABOVE everything else.
 * Its station shape is likewise trimmed to what scoring needs, with no station name, connector
 * types or per-level port counts. So the PDF export calls the two EXISTING endpoints once each
 * — `/api/poi/search` (all 12 categories) and `/api/ev-stations/nearby` — and passes the results
 * here. Widening score.ts's category filter would have been the smaller diff and is deliberately
 * NOT done: those categories are an input to an F-term, and this card must not perturb scoring.
 *
 * WHY THIS FILE IS PURE. Every ring is derived by measuring distance against arrays the caller
 * already has, so presenting two radii (or five) costs no extra external calls, and the whole
 * module can be pinned exactly by unit test with no network and no database.
 *
 * WHY IT LIVES IN THE WEB APP. Its only consumer is the PDF export, which runs in the browser and
 * is what calls the two endpoints. Putting it in apps/api would have meant a mirrored copy here
 * (the repo has no shared package — see auth/entitlements.ts for that pattern and its cost), and
 * a mirror of live logic is a drift risk with nothing on the API side to justify it.
 *
 * WHAT IT DELIBERATELY DOES NOT REUSE. `v2.competition.weighted` is DCFC 1.0 + L2-only 0.4 — a
 * scoring input, not a customer-facing quantity ("3.4 competitors" is not a sentence). And
 * `evCompetition.stationCount` counts every station fetched at the GATHER radius, not the ones
 * inside a presented ring: it is the closest-looking field to "competitors nearby" and it is the
 * wrong number.
 */
/**
 * Great-circle distance in miles. The same formula and the same Earth radius as the API's
 * rampDistance.haversineMiles — repeated rather than imported because the two apps build
 * separately and there is no shared package. A ten-line geodesic constant is the one kind of
 * duplication that carries no drift risk: it is mathematics, not a business rule.
 */
function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R_KM = 6371.0088
  const MILES_PER_KM = 0.621371
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * MILES_PER_KM
}

/** The two radii Noreen is choosing between. Presented together; neither is a commitment. */
export const DEFAULT_NEARBY_RADII_MI = [0.5, 1] as const

/**
 * Category order, taken from Noreen's own tool rather than invented here.
 *
 * It is domain judgement she wrote down — healthcare and education first, because they are the
 * highest-value EV partnership targets — and it is emphatically NOT a count ordering. Sorting by
 * count (this module's first cut) buries the single hospital under a row of restaurants, which
 * is exactly backwards for the person reading the page.
 */
export const NEARBY_CATEGORY_ORDER = [
  // Healthcare & education first — highest-value EV partnership targets
  'Hospital',
  'University',
  'Schools', // T-451 (F1): K-12 schools, slotted into the education band right after University
  // Commercial / retail
  'Restaurant',
  'Café',
  'Grocery',
  'Shopping',
  'Gas Station',
  'Parking',
  'Hotel/Motel',
  'Gym/Fitness',
  'Cinema',
  // Civic (single bucket)
  'Civic',
] as const

/**
 * Display-only overrides. The DATA key stays 'Café' — it is what our taxonomy, the map overlay
 * and `/api/poi/search` all use — and only the printed label differs, because what this counts
 * is true coffee chains. Renaming the key to match the label would silently break the map.
 */
export const NEARBY_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  Café: 'Coffee Shop',
  Parking: 'Parking Lots',
}

/**
 * Noreen's curated anchor brands. Her version matched these against OSM `brand`/`name`/`operator`
 * tags; ours matches the one name our provider gives us (`NormalizedPOI.name`). Her OSM-specific
 * 50-metre de-duplication is NOT carried over: it existed to collapse the node+way pair OSM emits
 * for one building, and ALS returns one record per place with a stable PlaceId, so de-duplicating
 * by id is the faithful translation of the same intent.
 */
export const NOTABLE_BRANDS = [
  // Big-box & retail
  'Target',
  'Walmart',
  'Costco',
  'Whole Foods',
  "Trader Joe's",
  'IKEA',
  'Home Depot',
  "Lowe's",
  // Entertainment
  'AMC',
  'Regal',
  'Cinemark',
  // Daily-traffic anchors
  'Starbucks',
  "Dunkin'",
  "McDonald's",
  'Chipotle',
  'CVS',
  'Walgreens',
  '7-Eleven',
] as const

/** The label used for a station with no operator. Never blank — a blank reads as missing data. */
export const NON_NETWORKED = 'Non-Networked'

/**
 * The station shape `/api/ev-stations/nearby` returns. Structural, not imported from the route,
 * so tests need no fixtures — and note it is NOT score.ts's internal AfdcStation, which carries
 * neither name nor connector types.
 */
export interface NearbyStation {
  name?: string | null
  lat: number | null
  lng: number | null
  network?: string | null
  connectorTypes?: readonly string[] | null
  portsLevel2?: number | null
  portsDcFast?: number | null
}

/** The subset of a NormalizedPOI (`/api/poi/search`) this module reads. */
export interface NearbyPoi {
  id?: string
  lat: number
  lng: number
  category: string
  name?: string
}

export interface NearbyRing {
  radiusMi: number
  /** Charging stations inside this ring. Cumulative: a station at 0.3 mi is in 0.5 AND in 1.0. */
  stations: number
  /** Of `stations`, how many offer at least one DC fast port. */
  dcFastStations: number
  /** Of `stations`, how many offer a NACS/Tesla connector. */
  nacsStations: number
  l2Ports: number
  dcFastPorts: number
  /** L2 + DC fast. L1 is excluded: AFDC's L1 reporting is inconsistent and an L1 outlet is not
   *  competition for a fast-charging site. Same call Noreen's tool made. */
  totalPorts: number
  /** Distinct BRANDED networks. An unbranded station is not a network — see nonNetworkedStations. */
  networks: number
  nonNetworkedStations: number
  /** Branded network holding the most ports here, or null when there is no branded network. */
  topNetwork: string | null
  /** topNetwork's share of ALL ports in this ring (0–1), non-networked ports included in the
   *  denominator — so a big name among independents reads as the minority it is. */
  topNetworkPortShare: number
  /** Total categorized places inside this ring. */
  places: number
  /** Category → count. Every key in `categories` is present, including explicit zeroes. */
  amenities: Record<string, number>
  /** Notable brand → count. Absent brands are simply absent. */
  brands: Record<string, number>
}

export interface NearestStation {
  network: string
  name: string
  distanceMi: number
  portsLevel2: number
  portsDcFast: number
  hasNacs: boolean
}

export interface NearbyStats {
  rings: NearbyRing[]
  /** Closest station with usable coordinates ANYWHERE in the gathered set — deliberately not
   *  ring-limited, so "nearest station: 3.1 mi" is a fact rather than a blank that would read
   *  as "no competition". Null only when nothing was gathered. */
  nearest: NearestStation | null
  /** Closest station belonging to a DIFFERENT network than `nearest`. */
  nearestAlt: NearestStation | null
  /** Categories worth a row, in Noreen's curated order; empty-everywhere categories dropped and
   *  anything outside her list appended rather than discarded. */
  categories: string[]
  /** Notable brands present in the widest ring, strongest first. */
  brands: Array<{ brand: string; count: number }>
  /**
   * True when the POI fetch came back at its result cap, which makes every amenity count a FLOOR
   * rather than a count. The renderer MUST say so — a capped number and a real number look
   * identical on the page, and only one of them is a fact.
   */
  amenitiesAreFloor: boolean
  /** The same statement for stations. See assessment note in `summarizeNearby`. */
  stationsAreFloor: boolean
  /** The ceiling that was in force, so a renderer can STATE the bound rather than hand-wave it. */
  poiResultCap: number
  gatherRadiusMi: number
  stationGatherRadiusMi: number
}

export interface NearbyOptions {
  /** Radius the caller fetched POIs at. No ring may exceed it. */
  gatherRadiusMi: number
  /** Radius the caller fetched STATIONS at; defaults to gatherRadiusMi. Gathering wider is what
   *  keeps the nearest-station line answerable outside dense areas. */
  stationGatherRadiusMi?: number
  /** RAW items the POI provider returned, BEFORE the 12-category filter dropped any. Not
   *  `pois.length` — see the floor assessment below. */
  poiFetched: number
  /** The provider's max-results ceiling for that POI fetch (ALS SearchNearby: 100). */
  poiResultCap: number
  /** The limit the station fetch was made with, if the caller wants truncation assessed. */
  stationResultCap?: number
  radiiMi?: readonly number[]
}

const hasNacsConnector = (s: NearbyStation): boolean =>
  (s.connectorTypes ?? []).some((t) => {
    const v = (t ?? '').toUpperCase()
    return v === 'TESLA' || v.includes('NACS')
  })

const normalizeNetwork = (n: string | null | undefined): string => {
  const v = (n ?? '').trim()
  return !v || v.toLowerCase() === 'non-networked' ? NON_NETWORKED : v
}

const describeStation = (s: NearbyStation, mi: number): NearestStation => ({
  network: normalizeNetwork(s.network),
  name: s.name ?? '',
  distanceMi: Math.round(mi * 100) / 100,
  portsLevel2: s.portsLevel2 ?? 0,
  portsDcFast: s.portsDcFast ?? 0,
  hasNacs: hasNacsConnector(s),
})

/** First curated brand whose name appears in the place name, or null. One brand per place. */
export function matchNotableBrand(name: string | undefined): string | null {
  const haystack = (name ?? '').toLowerCase()
  if (!haystack) return null
  for (const brand of NOTABLE_BRANDS) {
    if (haystack.includes(brand.toLowerCase())) return brand
  }
  return null
}

/**
 * Bucket already-fetched stations and POIs into concentric rings.
 *
 * Rings are CUMULATIVE by design. A station 0.3 mi away is inside the half-mile ring and inside
 * the one-mile ring, and it is counted in both. Bucketing exclusively would let a wider column
 * read LOWER than a narrower one, which cannot happen in reality and would be read as our bug.
 */
export function summarizeNearby(
  lat: number,
  lng: number,
  stations: readonly NearbyStation[],
  pois: readonly NearbyPoi[],
  opts: NearbyOptions,
): NearbyStats {
  const radii = [...(opts.radiiMi ?? DEFAULT_NEARBY_RADII_MI)]
  if (radii.length === 0) throw new Error('summarizeNearby: at least one radius is required')
  for (let i = 1; i < radii.length; i++) {
    if (radii[i]! <= radii[i - 1]!) {
      throw new Error(
        `summarizeNearby: radii must be strictly ascending, got [${radii.join(', ')}] — ` +
          'columns are read left-to-right as widening rings',
      )
    }
  }
  const widest = radii[radii.length - 1]!
  const stationGatherRadiusMi = opts.stationGatherRadiusMi ?? opts.gatherRadiusMi
  if (widest > opts.gatherRadiusMi) {
    // The silent-truncation case, and the reason this throws rather than warns: the outer column
    // would read "few amenities nearby" when it actually means "we never looked that far", and
    // nothing downstream could tell the difference.
    throw new Error(
      `summarizeNearby: widest ring ${widest} mi exceeds the POI gather radius ${opts.gatherRadiusMi} mi — ` +
        'that ring would be truncated and read as a real, low count',
    )
  }
  if (widest > stationGatherRadiusMi) {
    throw new Error(
      `summarizeNearby: widest ring ${widest} mi exceeds the station gather radius ` +
        `${stationGatherRadiusMi} mi — that ring would be truncated and read as a real, low count`,
    )
  }

  // Measure once; every ring reads from these.
  // Stations closer than ~250 ft (0.05 mi) are excluded to avoid counting the site's own
  // chargers — when a user clicks an existing station, it sits at distance 0 from itself.
  const SELF_EXCLUSION_MI = 0.05
  const stationDist = stations
    .map((s) =>
      typeof s.lat === 'number' && typeof s.lng === 'number'
        ? { s, mi: haversineMiles(lat, lng, s.lat, s.lng) }
        : null,
    )
    // AFDC coordinates are nullable. Coercing null to 0 would place the station off West Africa
    // and, far worse, report a nearest distance of 0 mi — which reads as "on this site".
    .filter((x): x is { s: NearbyStation; mi: number } => x !== null && x.mi > SELF_EXCLUSION_MI)
    .sort((a, b) => a.mi - b.mi)

  const poiDist = pois.map((p) => ({
    p,
    mi: haversineMiles(lat, lng, p.lat, p.lng),
    brand: matchNotableBrand(p.name),
  }))

  const rings: NearbyRing[] = radii.map((radiusMi) => {
    const near = stationDist.filter((x) => x.mi <= radiusMi)

    const netPorts = new Map<string, number>()
    let l2Ports = 0
    let dcFastPorts = 0
    let dcFastStations = 0
    let nacsStations = 0
    let nonNetworkedStations = 0
    for (const { s } of near) {
      const l2 = s.portsLevel2 ?? 0
      const dc = s.portsDcFast ?? 0
      l2Ports += l2
      dcFastPorts += dc
      if (dc > 0) dcFastStations++
      if (hasNacsConnector(s)) nacsStations++
      const net = normalizeNetwork(s.network)
      if (net === NON_NETWORKED) nonNetworkedStations++
      netPorts.set(net, (netPorts.get(net) ?? 0) + l2 + dc)
    }
    const totalPorts = l2Ports + dcFastPorts

    // Top network is chosen among BRANDED networks only; ties break by name so output is stable.
    let topNetwork: string | null = null
    let topPorts = 0
    for (const [net, ports] of [...netPorts].sort((a, b) => a[0].localeCompare(b[0]))) {
      if (net === NON_NETWORKED) continue
      if (topNetwork === null || ports > topPorts) {
        topNetwork = net
        topPorts = ports
      }
    }

    const amenities: Record<string, number> = {}
    const brands: Record<string, number> = {}
    let places = 0
    const seenPoi = new Set<string>()
    for (const { p, mi, brand } of poiDist) {
      if (mi > radiusMi) continue
      // De-duplicate by provider id — the faithful translation of Noreen's OSM node+way collapse.
      const key = p.id ?? ''
      if (key) {
        if (seenPoi.has(key)) continue
        seenPoi.add(key)
      }
      places++
      amenities[p.category] = (amenities[p.category] ?? 0) + 1
      if (brand) brands[brand] = (brands[brand] ?? 0) + 1
    }

    return {
      radiusMi,
      stations: near.length,
      dcFastStations,
      nacsStations,
      l2Ports,
      dcFastPorts,
      totalPorts,
      networks: [...netPorts.keys()].filter((n) => n !== NON_NETWORKED).length,
      nonNetworkedStations,
      topNetwork,
      topNetworkPortShare: totalPorts > 0 && topNetwork ? topPorts / totalPorts : 0,
      places,
      amenities,
      brands,
    }
  })

  // A category earns a row if it appears in ANY ring, presented in Noreen's curated order.
  // Anything outside her list is appended, not dropped: an unrecognised category is a signal we
  // want to see on the page, not something to swallow silently.
  const outer = rings[rings.length - 1]!
  const present = (c: string) => (outer.amenities[c] ?? 0) > 0
  const categories = [
    ...NEARBY_CATEGORY_ORDER.filter(present),
    ...Object.keys(outer.amenities)
      .filter((c) => !(NEARBY_CATEGORY_ORDER as readonly string[]).includes(c) && present(c))
      .sort(),
  ]

  // Backfill explicit zeroes so a renderer never has to distinguish "absent" from "none here" —
  // a 0 beside a 6 is information, and it should not depend on object-key presence.
  for (const ring of rings) {
    for (const c of categories) ring.amenities[c] = ring.amenities[c] ?? 0
  }

  const nearest =
    stationDist.length > 0 ? describeStation(stationDist[0]!.s, stationDist[0]!.mi) : null
  let nearestAlt: NearestStation | null = null
  if (nearest) {
    const alt = stationDist.find((x) => normalizeNetwork(x.s.network) !== nearest.network)
    if (alt) nearestAlt = describeStation(alt.s, alt.mi)
  }

  // FLOOR ASSESSMENT — the two ways these numbers could be presented as facts when they are not.
  //
  // POIs: `pois.length` cannot answer this. ALS caps at 100 items and everything outside our 12
  // categories is dropped before we see it, so a capped fetch of 100 raw places can arrive here
  // as 30 POIs; `pois.length >= cap` would be a check that essentially never fires — worse than
  // no check, because it reads as one that passed. The raw count is what the caller must pass.
  //
  // Stations: capped too, but gathered WIDER than the rings and returned nearest-first, so
  // truncation can only reach a ring if the farthest station we received is still inside the
  // widest one. That is derived from the returned distances rather than assumed from the
  // provider's ordering.
  const farthestStationMi = stationDist.length > 0 ? stationDist[stationDist.length - 1]!.mi : 0
  const stationsAreFloor =
    opts.stationResultCap !== undefined &&
    stations.length >= opts.stationResultCap &&
    farthestStationMi <= widest

  return {
    rings,
    nearest,
    nearestAlt,
    categories,
    brands: Object.entries(outer.brands)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([brand, count]) => ({ brand, count })),
    amenitiesAreFloor: opts.poiFetched >= opts.poiResultCap,
    stationsAreFloor,
    poiResultCap: opts.poiResultCap,
    gatherRadiusMi: opts.gatherRadiusMi,
    stationGatherRadiusMi,
  }
}

// ─── T-654: the single canonical amenity figure ──────────────────────────────────────────────
//
// Ricardo's product decision: the product's official "amenities near a
// site" figure is the category-targeted count at 0.25 mi, rendered as ONE canonical figure on
// BOTH the screen drill-down and the PDF one-pager. Both surfaces call THIS function on the SAME
// already-fetched nearby-POI set the /score/site payload carries, so they cannot contradict by
// construction — no second gather, no second radius, no second taxonomy.
//
// It re-buckets those POIs by their ALS `dist` (metres from the searched point, computed once
// server-side) rather than re-measuring here: reading the same server distance on both surfaces is
// a stronger guarantee than each surface re-deriving haversine from a point it holds separately.

/** The minimal shape this reads off a nearby POI — a category and its distance from the site. */
export interface CanonicalAmenityInput {
  /** Provider place id, used to de-duplicate (ALS returns one record per place). */
  id?: string
  category: string
  /** Metres from the searched site (ALS Distance). A POI with no distance cannot be placed in the
   *  ring and is not counted — an honest omission beats a guessed-in inclusion. */
  dist?: number
}

/** One printed amenity row: the data category, its customer-facing label, and the count. */
export interface CanonicalAmenityRow {
  category: string
  label: string
  count: number
}

/** The single canonical amenity figure both surfaces render. */
export interface CanonicalNearbyAmenities {
  /** The radius the counts are measured at (miles) — the F-term POI radius (default 0.25). */
  radiusMi: number
  /** One row per present category, in Noreen's curated order (healthcare/education first). */
  rows: CanonicalAmenityRow[]
  /** Total categorized places within the radius (sum of row counts). */
  total: number
  /**
   * True when the supplemental gather came back at its result cap, which makes every count a FLOOR
   * (AT LEAST the number shown) rather than an exact total — the same T-644 truncation signal, now
   * shared by both surfaces. The renderer marks floored counts and says so.
   */
  areFloor: boolean
  /** The result ceiling that gather ran under, so a renderer can STATE the bound. */
  poiResultCap: number
}

const CANONICAL_MILES_TO_METRES = 1609.34

export function canonicalNearbyAmenities(
  pois: readonly CanonicalAmenityInput[],
  opts: { radiusMi: number; poiFetched: number; poiResultCap: number },
): CanonicalNearbyAmenities {
  const radiusM = opts.radiusMi * CANONICAL_MILES_TO_METRES
  const counts: Record<string, number> = {}
  let total = 0
  const seen = new Set<string>()
  for (const p of pois) {
    // No distance → cannot confirm it is inside the ring. Excluded, not guessed in.
    if (typeof p.dist !== 'number' || p.dist > radiusM) continue
    const key = p.id ?? ''
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    counts[p.category] = (counts[p.category] ?? 0) + 1
    total++
  }

  // Present categories in Noreen's curated order first, then any unrecognised category appended
  // (sorted) rather than dropped — an unknown category is a signal, not noise. Mirrors summarizeNearby.
  const present = (c: string): boolean => (counts[c] ?? 0) > 0
  const ordered = [
    ...NEARBY_CATEGORY_ORDER.filter(present),
    ...Object.keys(counts)
      .filter((c) => !(NEARBY_CATEGORY_ORDER as readonly string[]).includes(c) && present(c))
      .sort(),
  ]
  const rows: CanonicalAmenityRow[] = ordered.map((c) => ({
    category: c,
    label: NEARBY_CATEGORY_LABELS[c] ?? c,
    count: counts[c]!,
  }))

  return {
    radiusMi: opts.radiusMi,
    rows,
    total,
    areFloor: opts.poiFetched >= opts.poiResultCap,
    poiResultCap: opts.poiResultCap,
  }
}
